import { Injectable } from '@angular/core';
import {
  CommunicationChannel,
  DeviceResult,
  InverterData,
  InverterDataComposer,
  ModbusCommandOps,
  RegisterTable,
  ok,
  err,
  transportError,
  BatteryDetails,
  ComposeV3BatteryWizardVersion,
  ComposeV3DeviceBatteryConfig,
  ComposeV3BduDetails,
  ComposeV3BmsDetails,
  ComposeV3BmsRealtime,
  ComposeV3BmsFaults,
  ComposeV3PcuDetails,
  V3BatterySettings,
  V3BatteryWizardVersionInfo,
  V3BatteryProtocolWizardVersionInfo,
  V3BduConfig,
  V3BduDetails,
  V3BmsDetails,
  V3BmsFaults,
  V3BmsRealtime,
  V3DeviceBatteryConfig,
  V3DeviceInputTypeChannelConfig,
  V3InputTypeChannelConfig,
  V3PcuConfig,
  V3PcuDetails,
  V3WizardVersionInfo,
  FirmwareInfo,
} from 'ngx-modbus';
import { Observable, Subject } from 'rxjs';
import { startWith, switchMap, shareReplay } from 'rxjs/operators';

import { BlockTranslationsService } from '../block-translations.service';

// ─── ModbusReadService ────────────────────────────────────────────────────────
//
// Injectable replacement for the @CreateInfoObservable decorator.
// Returns Observable<DeviceResult<T>> instead of throwing/toasting.
//
// Usage:
//   this.data$ = this.modbusRead.read$(channel, refresh$, table, composer, options);
//
// The consumer handles errors via Result pattern matching or the
// handleModbusError() RxJS operator (Phase 3 - ErrorSink).
//
// ── Compose strategy ──────────────────────────────────────────────────────
//
// Two distinct patterns depending on whether registers are multiplexed:
//
// NON-MULTIPLEXED (read$, readOnce):
//   The composer is passed to executeMixedRequestsSafe AND applied locally
//   via applyComposer(). This means the composer runs twice: once inside
//   decodeAndMergeRequests (result stored in ModbusLink._lastComposedSnapshot),
//   and once here (result returned to the consumer via the Observable).
//   Both produce identical output because they start from the same raw data.
//   The double-compose is intentional — it keeps composedData in sync for
//   consumers that access it directly (e.g. device-upgrade, device-chat-bot).
//
// MULTIPLEXED (getBatteryDetails, retrieveBDUInfo, retrieveBmsAndRtInfo, etc.):
//   A selector register (BAT_CONFIG_ID, BDU_Inquire, BMS_Inquire, PCU_Inquire)
//   switches which physical unit is addressed. The same register addresses
//   return different values depending on the selector. For each unit:
//     1. WRITE selector → point to unit N
//     2. READ table → same addresses, values now belong to unit N
//     3. Snapshot: rawSnapshot = { ...channel.command.data }
//     4. Compose locally: composed = applyComposer(rawSnapshot, Composer)
//     5. Store in array: container[N] = composed
//   The composer is NOT passed to executeMixedRequestsSafe — compose happens
//   only locally on the snapshot. This is critical because:
//   - this.content is overwritten on each iteration (unit N+1 overwrites N)
//   - The snapshot freezes the raw data before the next write+read cycle
//   - composedData after multiplexing contains only the LAST unit's raw data
//   - registerReadCache after multiplexing contains bytes of the LAST unit
//     (do NOT use preferCache with multiplexed tables)

export interface ReadOptions extends ModbusCommandOps {
  /** If true, errors are returned as err() without logging warnings. */
  optional?: boolean;
  /** On first emission, skip device read if all registers are cached (e.g. from prefetch). */
  preferCache?: boolean;
}

const MODBUS_READ_DEBUG = { ENABLED: true };

@Injectable({
  providedIn: 'root',
})
export class ModbusReadService {

  constructor(private readonly blockTranslations: BlockTranslationsService) {}

  /** Apply composer functions locally on raw data (no shared state). */
  private applyComposer(
    data: Partial<InverterData>,
    composer?: Partial<InverterDataComposer>,
  ): Partial<InverterData> {
    if (!composer) return data;
    let result = { ...data };
    for (const [, fn] of Object.entries(composer)) {
      if (fn) result = fn({ ...result });
    }
    return result;
  }

  /**
   * Observable that reads modbus registers on each refresh emission.
   * Returns Result — never throws, never shows toasts.
   */
  read$(
    channel: CommunicationChannel,
    refresh$: Subject<void>,
    table: Partial<RegisterTable>,
    composer?: Partial<InverterDataComposer>,
    options?: ReadOptions,
  ): Observable<DeviceResult<Partial<InverterData>>> {
    let initialCacheChecked = false;

    return refresh$.pipe(
      startWith(null),
      switchMap(() => new Promise(resolve => window.requestAnimationFrame(resolve))),
      switchMap(async () => {
        if (!channel.command) {
          return err(transportError('disconnected', { message: 'ModbusLink not available' })) as DeviceResult<Partial<InverterData>>;
        }

        // Cache short-circuit on first emission only (e.g. from prefetch)
        if (!initialCacheChecked && options?.preferCache) {
          initialCacheChecked = true;
          if (channel.command.hasAllRegistersInCache(table)) {
            return ok(this.applyComposer(
              channel.command.data ?? {} as Partial<InverterData>,
              composer,
            ));
          }
        }

        const modbusOpts: ModbusCommandOps = {
          ...options,
          concurrentQuery: options?.concurrentQuery ?? channel.concurrentQuery,
        };

        // Composer is passed to executeMixedRequestsSafe so that composedData
        // stays in sync (see "Compose strategy" in the header). The local
        // applyComposer below produces the same result from the same raw data.
        const result = await channel.command.executeMixedRequestsSafe([{
          mode: 'read',
          table,
          composer,
          options: modbusOpts,
        }]);

        return result.map(() => this.applyComposer(
          channel.command?.data ?? {} as Partial<InverterData>,
          composer,
        ));
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * One-shot read of registers. Returns Result.
   */
  async readOnce(
    channel: CommunicationChannel,
    table: Partial<RegisterTable>,
    composer?: Partial<InverterDataComposer>,
    options?: ReadOptions,
  ): Promise<DeviceResult<Partial<InverterData>>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    const modbusOpts: ModbusCommandOps = {
      ...options,
      concurrentQuery: options?.concurrentQuery ?? channel.concurrentQuery,
    };

    // Composer is passed to executeMixedRequestsSafe so that composedData
    // stays in sync (see "Compose strategy" in the header). The local
    // applyComposer below produces the same result from the same raw data.
    const result = await channel.command.executeMixedRequestsSafe([{
      mode: 'read',
      table,
      composer,
      options: modbusOpts,
    }]);

    return result.map(() => this.applyComposer(
      channel.command?.data ?? {} as Partial<InverterData>,
      composer,
    ));
  }

  // ─── Specialized Reads (replace decorator methods) ──────────────────────

  /**
   * Reads battery configuration details for all batteries detected.
   * Replaces decorator's `getBatteryDetails`.
   */
  async getBatteryDetails(
    channel: CommunicationChannel,
  ): Promise<DeviceResult<BatteryDetails[]>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    // Step 1: Read input type channel config
    const configResult = await channel.command.executeMixedRequestsSafe([{
      mode: 'read',
      table: V3DeviceInputTypeChannelConfig,
      options: {
        maxRegisterPerPage: 0x40,
        concurrentQuery: channel.concurrentQuery,
        useValidityMask: 'AddressMask_Config_Basic1',
      },
    }]);

    if (configResult.isErr()) {
      return err(configResult.error);
    }

    // Step 2: Determine batteries from input type
    let batteriesInfo = Array.from({ length: V3InputTypeChannelConfig.Input_Type.length })
      .map((_, i) => ({ input: channel.command?.data[`INPUT_TYPE_CHANNEL_CONFIG${i}`] }))
      .filter(({ input }) => typeof input === 'number' && input > 127 && input <= 255)
      .map(({ input }) => ({
        inputId: (input as number) - 128,
        batteryLabel: this.blockTranslations.labels.deviceInfoBattery({ value: (input as number) - 127 }),
      }))
      .filter(({ inputId }, index, self) => self.findIndex(item => item.inputId === inputId) === index);

    batteriesInfo = batteriesInfo.length
      ? batteriesInfo
      : [{
        inputId: 0,
        batteryLabel: this.blockTranslations.labels.deviceInfoBattery({ value: 1 }),
      }];

    // Step 3: Write BAT_CONFIG_ID + read config for each battery.
    // Capture raw snapshot after each read, compose locally — no prefixed keys in ModbusLink.
    const batteryConfig: BatteryDetails[] = [];
    for (const bat of batteriesInfo) {
      const pairResult = await channel.command.executeMixedRequestsSafe([
        {
          mode: 'write',
          key: 'BAT_CONFIG_ID',
          value: bat.inputId,
          table: V3BatterySettings,
          options: { concurrentQuery: channel.concurrentQuery },
        },
        {
          mode: 'read',
          table: V3DeviceBatteryConfig,
          options: {
            concurrentQuery: channel.concurrentQuery,
            maxRegisterPerPage: 0x40,
          },
        },
      ]);
      if (pairResult.isErr()) {
        MODBUS_READ_DEBUG.ENABLED && console.warn(`[ModbusRead] getBatteryDetails: battery ${bat.inputId} failed, skipping`, pairResult.error);
        continue;
      }

      // Snapshot raw data and compose locally
      const rawSnapshot = { ...channel.command.data };
      const composed = this.applyComposer(rawSnapshot, ComposeV3DeviceBatteryConfig);
      const value: Partial<InverterData> = {};
      Object.keys(V3DeviceBatteryConfig).forEach(key => {
        value[key] = composed[key];
      });
      batteryConfig.push({ label: bat.batteryLabel, index: bat.inputId + 1, value });
    }

    return ok(batteryConfig);
  }

  /**
   * Reads firmware version info (ARM, DSPM, DSPS, SAFETY).
   * Replaces decorator's `getFirmwareInfo`.
   * Battery protocol/wizard reads are optional (may not exist on all devices).
   *
   * Unlike the retrieve* methods, this does NOT use snapshot+compose because:
   * - No multiplexing: no selector register, no iteration with overwrite.
   * - The 5 keys read for FirmwareInfo (SN, ARM, DSPM, DSPS, SAFETY_FIRMWARE_VERSION)
   *   are all ASCII strings from V3WizardVersionInfo — never touched by any composer.
   * - Battery version compose (PCU/BDU/BMS → display strings) is handled by passing
   *   ComposeV3BatteryWizardVersion to executeMixedRequestsSafe in Step 2+3.
   *   Consumers read those via command.composedData (e.g. device-upgrade).
   */
  async getFirmwareInfo(
    channel: CommunicationChannel,
  ): Promise<DeviceResult<FirmwareInfo | null>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    // Step 1: Read main version info (required)
    const mainResult = await channel.command.executeMixedRequestsSafe([{
      mode: 'read',
      table: V3WizardVersionInfo,
      options: { concurrentQuery: channel.concurrentQuery },
    }]);
    if (mainResult.isErr()) {
      return err(mainResult.error);
    }

    // Steps 2+3: Read battery protocol + wizard version in a single round-trip (both optional)
    await channel.command.executeMixedRequestsSafe([{
      mode: 'read',
      table: V3BatteryProtocolWizardVersionInfo,
      options: { concurrentQuery: channel.concurrentQuery },
    }, {
      mode: 'read',
      table: V3BatteryWizardVersionInfo,
      composer: ComposeV3BatteryWizardVersion,
      options: { concurrentQuery: channel.concurrentQuery },
    }]);
    // Ignore errors — optional

    const data = channel.command.data;
    if (!data?.SN || !data?.ARM || !data?.DSPM || !data?.DSPS) {
      return ok(null);
    }

    return ok({
      serial: data.SN as string,
      modules: {
        ARM: data.ARM as string,
        DSPM: data.DSPM as string,
        DSPS: data.DSPS as string,
        SAFETY: (data.SAFETY_FIRMWARE_VERSION || '') as string,
        SOLARMAN_LOGGER: '',
      },
    });
  }

  /**
   * Retrieve BDU details (write inquire + read details for each BDU).
   * Replaces decorator's `retrieveBDUInfo`.
   */
  async retrieveBDUInfo(
    channel: CommunicationChannel,
  ): Promise<DeviceResult<{ bduDetailsContainer: Array<Partial<InverterData>> }>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    const bduDetailsContainer: Array<Partial<InverterData>> = [];

    // Read first BDU to discover BDU_Number
    const initialResult = await channel.command.executeMixedRequestsSafe([
      { mode: 'write', key: 'BDU_Inquire', value: 1, table: V3BduConfig, options: { concurrentQuery: channel.concurrentQuery } },
      { mode: 'read', table: V3BduDetails, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
    ]);
    if (initialResult.isErr()) return err(initialResult.error);

    bduDetailsContainer[1] = this.applyComposer({ ...channel.command.data }, ComposeV3BduDetails);
    const bduNumber = channel.command.data?.BDU_Number as number ?? 0;

    for (let i = 2; i <= bduNumber; i++) {
      const pairResult = await channel.command.executeMixedRequestsSafe([
        { mode: 'write', key: 'BDU_Inquire', value: i, table: V3BduConfig, options: { concurrentQuery: channel.concurrentQuery } },
        { mode: 'read', table: V3BduDetails, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
      ]);
      if (pairResult.isErr()) {
        MODBUS_READ_DEBUG.ENABLED && console.warn(`[ModbusRead] retrieveBDUInfo: BDU ${i} failed, continuing`, pairResult.error);
        continue;
      }
      bduDetailsContainer[i] = this.applyComposer({ ...channel.command.data }, ComposeV3BduDetails);
    }

    return ok({ bduDetailsContainer });
  }

  /**
   * Retrieve BMS + Realtime details for all battery packs.
   * Replaces decorator's `retrieveBmsAndRtInfo`.
   */
  async retrieveBmsAndRtInfo(
    channel: CommunicationChannel,
  ): Promise<DeviceResult<{ bmsDetailsContainer: Array<Partial<InverterData>>; bmsRtContainer: Array<Partial<InverterData>> }>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    const composeBMSInquireValue = (series: number, parallel: number, fault = 0) =>
      (fault << 12) | (parallel << 8) | series;

    const bmsDetailsContainer: Array<Partial<InverterData>> = [];
    const bmsRtContainer: Array<Partial<InverterData>> = [];

    // Read first pack to discover BaPack_Parallel count
    const initialResult = await channel.command.executeMixedRequestsSafe([
      { mode: 'write', key: 'BMS_Inquire', value: composeBMSInquireValue(0, 0), table: V3BmsDetails, options: { concurrentQuery: channel.concurrentQuery } },
      { mode: 'read', table: V3BmsDetails, options: { maxRegisterPerPage: 0x20, concurrentQuery: channel.concurrentQuery } },
      { mode: 'read', table: V3BmsRealtime, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
    ]);
    if (initialResult.isErr()) return err(initialResult.error);

    const rawSnapshot0 = { ...channel.command.data };
    bmsDetailsContainer[0] = this.applyComposer(rawSnapshot0, ComposeV3BmsDetails);
    bmsRtContainer[0] = this.applyComposer(rawSnapshot0, ComposeV3BmsRealtime);

    const BMS_BaPack_Parallel = ((rawSnapshot0.BaPack_Number as number) >> 8) || 0;
    for (let i = 1; i < BMS_BaPack_Parallel; i++) {
      const pairResult = await channel.command.executeMixedRequestsSafe([
        { mode: 'write', key: 'BMS_Inquire', value: composeBMSInquireValue(0, i), table: V3BmsDetails, options: { concurrentQuery: channel.concurrentQuery } },
        { mode: 'read', table: V3BmsDetails, options: { maxRegisterPerPage: 0x20, concurrentQuery: channel.concurrentQuery } },
        { mode: 'read', table: V3BmsRealtime, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
      ]);
      if (pairResult.isErr()) {
        MODBUS_READ_DEBUG.ENABLED && console.warn(`[ModbusRead] retrieveBmsAndRtInfo: pack ${i} failed, continuing`, pairResult.error);
        continue;
      }
      const rawSnapshotI = { ...channel.command.data };
      bmsDetailsContainer[i] = this.applyComposer(rawSnapshotI, ComposeV3BmsDetails);
      bmsRtContainer[i] = this.applyComposer(rawSnapshotI, ComposeV3BmsRealtime);
    }

    return ok({ bmsDetailsContainer, bmsRtContainer });
  }

  /**
   * Retrieve PCU details + config for all PCUs.
   * Replaces decorator's `retrievePCUInfo`.
   */
  async retrievePCUInfo(
    channel: CommunicationChannel,
  ): Promise<DeviceResult<{ pcuDetailsContainer: Array<Partial<InverterData>>; pcuConfigContainer: Array<Partial<InverterData>> }>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    const pcuDetailsContainer: Array<Partial<InverterData>> = [];
    const pcuConfigContainer: Array<Partial<InverterData>> = [];

    // Read first PCU to discover PCU_Number
    const initialResult = await channel.command.executeMixedRequestsSafe([
      { mode: 'write', key: 'PCU_Inquire', value: 1, table: V3PcuConfig, options: { concurrentQuery: channel.concurrentQuery } },
      { mode: 'read', table: V3PcuDetails, options: { maxRegisterPerPage: 0x40, useValidityMask: 'AddressMask_PCU1', concurrentQuery: channel.concurrentQuery } },
      { mode: 'read', table: V3PcuConfig, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
    ]);
    if (initialResult.isErr()) return err(initialResult.error);

    const rawSnapshot1 = { ...channel.command.data };
    pcuDetailsContainer[1] = this.applyComposer(rawSnapshot1, ComposeV3PcuDetails);
    pcuConfigContainer[1] = { ...rawSnapshot1 };
    const PCU_Number = (rawSnapshot1.PCU_Number as number) ?? 0;

    for (let i = 2; i <= PCU_Number; i++) {
      const pairResult = await channel.command.executeMixedRequestsSafe([
        { mode: 'write', key: 'PCU_Inquire', value: i, table: V3PcuConfig, options: { concurrentQuery: channel.concurrentQuery } },
        { mode: 'read', table: V3PcuDetails, options: { maxRegisterPerPage: 0x40, useValidityMask: 'AddressMask_PCU1', concurrentQuery: channel.concurrentQuery } },
        { mode: 'read', table: V3PcuConfig, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
      ]);
      if (pairResult.isErr()) {
        MODBUS_READ_DEBUG.ENABLED && console.warn(`[ModbusRead] retrievePCUInfo: PCU ${i} failed, continuing`, pairResult.error);
        continue;
      }
      const rawSnapshotI = { ...channel.command.data };
      pcuDetailsContainer[i] = this.applyComposer(rawSnapshotI, ComposeV3PcuDetails);
      pcuConfigContainer[i] = { ...rawSnapshotI };
    }

    return ok({ pcuDetailsContainer, pcuConfigContainer });
  }

  /**
   * Retrieve BMS Fault Info for a specific fault index.
   * Replaces decorator's `retrieveBmsFaultInfo`.
   */
  async retrieveBmsFaultInfo(
    channel: CommunicationChannel,
    packIndex: number,
    stringIndex: number,
    faultIndex: number,
  ): Promise<DeviceResult<Partial<InverterData> | null>> {
    if (!channel.command) {
      return err(transportError('disconnected', { message: 'ModbusLink not available' }));
    }

    const inquireValue = (faultIndex << 12) | (stringIndex << 8) | packIndex;

    const result = await channel.command.executeMixedRequestsSafe([
      { mode: 'write', key: 'BMS_Inquire', value: inquireValue, table: V3BmsDetails, options: { concurrentQuery: channel.concurrentQuery } },
      { mode: 'read', table: V3BmsFaults, options: { maxRegisterPerPage: 0x40, concurrentQuery: channel.concurrentQuery } },
    ]);

    if (result.isErr()) {
      return ok(null); // Fault read failure is non-critical
    }

    // Snapshot raw and compose locally
    const faultData = this.applyComposer({ ...channel.command.data }, ComposeV3BmsFaults);

    if (faultData.Pack_Fault_Timestamp === 0 || faultData.Pack_Fault_Timestamp === undefined) {
      return ok(null);
    }

    return ok(faultData);
  }
}
